import { motion, useInView, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 40, className = "", ...props }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
    {...props}
  >
    {children}
  </motion.div>
);

export const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden -mb-[0.15em] ${className}`}>
    <motion.span
      className="block pb-[0.15em]"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Counter = ({ value, suffix = "", className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const duration = 1800;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 4);
      setDisplay(Math.round(eased * value));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return <span ref={ref} className={className}>{display}{suffix}</span>;
};

export const RotatingText = ({ phrases, interval = 2800, className = "text-gradient" }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % phrases.length), interval);
    return () => clearInterval(t);
  }, [phrases.length, interval]);
  return (
    <span className="inline-grid overflow-visible align-baseline" data-testid="rotating-text">
      {phrases.map((p) => (
        <span key={p} aria-hidden="true" className={`invisible pb-[0.15em] ${className}`} style={{ gridArea: "1 / 1" }}>{p}</span>
      ))}
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={phrases[i]}
          initial={{ opacity: 0, y: 20, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -20, filter: "blur(10px)" }}
          transition={{ duration: 0.7, ease: EASE }}
          className={`pb-[0.15em] ${className}`}
          style={{ gridArea: "1 / 1" }}
        >
          {phrases[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

export const SectionHeading = ({ overline, title, description, dark }) => (
  <div className="max-w-3xl mb-16 md:mb-20">
    <Reveal>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-5">{overline}</p>
    </Reveal>
    <Reveal delay={0.1}>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.1]">{title}</h2>
    </Reveal>
    {description && (
      <Reveal delay={0.2}>
        <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">{description}</p>
      </Reveal>
    )}
  </div>
);
